import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Input, Select, Tag, Typography, Alert, Popconfirm, Empty, Space, theme,
} from 'antd';
import {
  FolderOpenOutlined, PlusOutlined, DeleteOutlined, EditOutlined, ExportOutlined,
  BookOutlined, ApartmentOutlined, FileDoneOutlined, SafetyCertificateOutlined,
  FileImageOutlined, CloudOutlined, SearchOutlined, AuditOutlined, FileOutlined,
  SettingOutlined, BankOutlined, CloseOutlined,
} from '@ant-design/icons';
import { ProjectDocument, DocumentType, SystemKey } from '../../models/types';
import {
  createProjectDocument, getProjectDocuments,
  updateProjectDocument, deleteProjectDocument,
} from '../../firebase/documents';
import { useAuth } from '../../context/AuthContext';

interface Props { projectId: string; projectName?: string; }

const DOC_TYPES: DocumentType[] = [
  'O&M Manual',
  'Sequence of Operations',
  'Commissioning Report',
  'Warranty',
  'Drawing / As-Built',
  'TAB Report',
  'Inspection Report',
  'Permit',
  'Other',
];

const SYSTEMS: { key: SystemKey; label: string }[] = [
  { key: 'controls', label: 'Controls' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'mechanical', label: 'Mechanical' },
  { key: 'plumbing', label: 'Plumbing' },
];

const TYPE_ICONS: Record<DocumentType, React.ReactNode> = {
  'O&M Manual': <BookOutlined />,
  'Sequence of Operations': <ApartmentOutlined />,
  'Commissioning Report': <FileDoneOutlined />,
  'Warranty': <SafetyCertificateOutlined />,
  'Drawing / As-Built': <FileImageOutlined />,
  'TAB Report': <CloudOutlined />,
  'Inspection Report': <SearchOutlined />,
  'Permit': <AuditOutlined />,
  'Other': <FileOutlined />,
};

const emptyForm = (): Omit<ProjectDocument, 'id' | 'projectId' | 'createdAt' | 'updatedAt' | 'addedBy'> => ({
  name: '',
  type: 'O&M Manual',
  url: '',
  assetDescription: '',
  system: undefined,
  notes: '',
});

function isValidUrl(s: string): boolean {
  try { new URL(s); return true; } catch { return false; }
}

const toOption = (v: string) => ({ value: v, label: v });

const DocumentLibrary: React.FC<Props> = ({ projectId, projectName }) => {
  const { currentUser } = useAuth();
  const { token } = theme.useToken();
  const [docs, setDocs] = useState<ProjectDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(emptyForm());

  const [filterType, setFilterType] = useState<string>('all');
  const [filterSystem, setFilterSystem] = useState<string>('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setDocs(await getProjectDocuments(projectId)); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) { setFormError('Document name is required.'); return; }
    if (!form.url.trim()) { setFormError('URL is required.'); return; }
    if (!isValidUrl(form.url)) { setFormError('Please enter a valid URL (e.g. https://drive.google.com/...).'); return; }
    setFormError(null); setSaving(true);
    try {
      await createProjectDocument({
        projectId,
        name: form.name.trim(),
        type: form.type,
        url: form.url.trim(),
        assetDescription: form.assetDescription?.trim() || undefined,
        system: form.system || undefined,
        notes: form.notes?.trim() || undefined,
        addedBy: currentUser?.email ?? undefined,
      });
      setShowForm(false); setForm(emptyForm()); await load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const startEdit = (d: ProjectDocument) => {
    setEditingId(d.id);
    setEditForm({ name: d.name, type: d.type, url: d.url, assetDescription: d.assetDescription ?? '', system: d.system, notes: d.notes ?? '' });
  };

  const saveEdit = async (id: string) => {
    if (!editForm.name.trim() || !editForm.url.trim()) return;
    try {
      await updateProjectDocument(id, {
        name: editForm.name.trim(), type: editForm.type, url: editForm.url.trim(),
        assetDescription: editForm.assetDescription?.trim() || undefined,
        system: editForm.system || undefined,
        notes: editForm.notes?.trim() || undefined,
      });
      setEditingId(null); await load();
    } catch (e: any) { setError(e.message); }
  };

  const remove = async (id: string) => {
    try { await deleteProjectDocument(id); await load(); }
    catch (e: any) { setError(e.message); }
  };

  // Filtered docs
  const filtered = docs.filter(d => {
    if (filterType !== 'all' && d.type !== filterType) return false;
    if (filterSystem !== 'all' && d.system !== filterSystem) return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase()) &&
        !d.assetDescription?.toLowerCase().includes(search.toLowerCase()) &&
        !d.notes?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by type for the grid view
  const grouped = DOC_TYPES.reduce((acc, t) => {
    const items = filtered.filter(d => d.type === t);
    if (items.length > 0) acc[t] = items;
    return acc;
  }, {} as Record<DocumentType, ProjectDocument[]>);

  if (loading) return <Card loading />;

  const docGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 };
  const formGrid: React.CSSProperties = { display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' };

  const field = (label: string, node: React.ReactNode, span?: boolean) => (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      {node}
    </div>
  );

  const metaLine = (icon: React.ReactNode, text: React.ReactNode, extra?: React.CSSProperties) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: token.colorTextSecondary, marginTop: 6, ...extra }}>
      {icon}{text}
    </div>
  );

  return (
    <div>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}><FolderOpenOutlined /> Documents</Typography.Title>
            <Typography.Text type="secondary">
              {projectName ? `${projectName} · ` : ''}{docs.length} document{docs.length !== 1 ? 's' : ''}
            </Typography.Text>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Button type="primary" icon={showForm ? <CloseOutlined /> : <PlusOutlined />} onClick={() => setShowForm(v => !v)}>
              {showForm ? 'Cancel' : 'Add Document'}
            </Button>
          </div>
        </div>
      </Card>

      {showForm && (
        <Card title="Add document" style={{ marginBottom: 16 }}>
          <div style={formGrid}>
            {field('Document Name *', (
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. AHU-1 O&M Manual" autoFocus />
            ))}
            {field('Document Type *', (
              <Select style={{ width: '100%' }} value={form.type} options={DOC_TYPES.map(toOption)} onChange={v => setForm(f => ({ ...f, type: v as DocumentType }))} />
            ))}
            {field('URL / Link *', (
              <>
                <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://drive.google.com/… or https://sharepoint.com/…" />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Paste a link to Google Drive, SharePoint, OneDrive, Dropbox, or any web URL.</Typography.Text>
              </>
            ), true)}
            {field('Related Asset / Equipment', (
              <Input value={form.assetDescription} onChange={e => setForm(f => ({ ...f, assetDescription: e.target.value }))} placeholder="e.g. AHU-1, Boiler, Fire Panel" />
            ))}
            {field('System', (
              <Select
                style={{ width: '100%' }} value={form.system ?? ''}
                options={[{ value: '', label: '— All systems / building-wide —' }, ...SYSTEMS.map(s => ({ value: s.key, label: s.label }))]}
                onChange={v => setForm(f => ({ ...f, system: (v as SystemKey) || undefined }))}
              />
            ))}
            {field('Notes', (
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Rev 3, issued at closeout, Trane model X123" />
            ), true)}
          </div>
          {formError && <Alert type="error" showIcon message={formError} style={{ marginTop: 14 }} />}
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" loading={saving} onClick={save}>Save</Button>
            <Button onClick={() => { setShowForm(false); setForm(emptyForm()); setFormError(null); }}>Cancel</Button>
          </Space>
        </Card>
      )}

      {docs.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
          <Input
            allowClear prefix={<SearchOutlined style={{ color: token.colorTextQuaternary }} />}
            style={{ width: 220 }} placeholder="Search documents…"
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <Select
            style={{ minWidth: 150 }} value={filterType}
            options={[{ value: 'all', label: 'All Types' }, ...DOC_TYPES.map(toOption)]}
            onChange={setFilterType}
          />
          <Select
            style={{ minWidth: 140 }} value={filterSystem}
            options={[{ value: 'all', label: 'All Systems' }, ...SYSTEMS.map(s => ({ value: s.key, label: s.label }))]}
            onChange={setFilterSystem}
          />
          {(search || filterType !== 'all' || filterSystem !== 'all') && (
            <Button icon={<CloseOutlined />} onClick={() => { setSearch(''); setFilterType('all'); setFilterSystem('all'); }}>Clear</Button>
          )}
        </div>
      )}

      {docs.length === 0 && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={(
              <>
                <Typography.Title level={5} style={{ marginBottom: 4 }}>No documents yet</Typography.Title>
                <Typography.Text type="secondary">Add links to O&M manuals, sequences of operations, commissioning reports, drawings, and warranties.</Typography.Text>
              </>
            )}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowForm(true)}>Add First Document</Button>
          </Empty>
        </Card>
      )}

      {filtered.length === 0 && docs.length > 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No documents match the current filter." style={{ padding: '24px 0' }} />
      )}

      {Object.entries(grouped).map(([type, items]) => (
        <div key={type} style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
            <span style={{ color: token.colorPrimary }}>{TYPE_ICONS[type as DocumentType]}</span>
            <Typography.Text strong style={{ fontSize: 14 }}>{type}</Typography.Text>
            <Tag color="green" style={{ marginInlineEnd: 0 }}>{items.length}</Tag>
          </div>
          <div style={docGrid}>
            {items.map(d => (
              editingId === d.id ? (
                <Card key={d.id} size="small" style={{ borderColor: token.colorPrimaryBorder }}>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" />
                    <Input value={editForm.url} onChange={e => setEditForm(f => ({ ...f, url: e.target.value }))} placeholder="URL" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Select
                        style={{ width: '100%' }} value={editForm.type} options={DOC_TYPES.map(toOption)}
                        onChange={v => setEditForm(f => ({ ...f, type: v as DocumentType }))}
                      />
                      <Select
                        style={{ width: '100%' }} value={editForm.system ?? ''}
                        options={[{ value: '', label: 'No system' }, ...SYSTEMS.map(s => ({ value: s.key, label: s.label }))]}
                        onChange={v => setEditForm(f => ({ ...f, system: (v as SystemKey) || undefined }))}
                      />
                    </div>
                    <Input value={editForm.assetDescription} onChange={e => setEditForm(f => ({ ...f, assetDescription: e.target.value }))} placeholder="Asset / equipment" />
                    <Input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" />
                    <Space>
                      <Button type="primary" size="small" onClick={() => saveEdit(d.id)}>Save</Button>
                      <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                    </Space>
                  </Space>
                </Card>
              ) : (
                <Card key={d.id} size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <a
                      href={d.url} target="_blank" rel="noopener noreferrer"
                      style={{ fontWeight: 600, color: token.colorPrimary, display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}
                    >
                      {TYPE_ICONS[d.type]}{d.name}
                      <ExportOutlined style={{ fontSize: 11, opacity: 0.6 }} />
                    </a>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(d)} title="Edit" aria-label="Edit document" />
                      <Popconfirm title={`Delete "${d.name}"?`} onConfirm={() => remove(d.id)} okText="Delete" okButtonProps={{ danger: true }}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete" aria-label="Delete document" />
                      </Popconfirm>
                    </div>
                  </div>
                  {d.assetDescription && metaLine(<SettingOutlined />, d.assetDescription, { marginTop: 7 })}
                  {d.system && metaLine(<BankOutlined />, d.system, { textTransform: 'capitalize' })}
                  {d.notes && <div style={{ fontStyle: 'italic', marginTop: 6, fontSize: 12, color: token.colorTextTertiary }}>{d.notes}</div>}
                  {d.addedBy && <div style={{ marginTop: 4, fontSize: 12, color: token.colorTextTertiary }}>Added by {d.addedBy}</div>}
                </Card>
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentLibrary;
